/**
 * Mac Studio Window Component
 *
 * Frosted glass window with title bar and traffic lights.
 * Supports open/close animations, focus states, dragging, and resizing.
 */

import { ReactNode, useState, useEffect, useCallback, useMemo } from 'react'
import { TrafficLights } from './TrafficLights'
import { useDraggable } from '@/hooks/useDraggable'
import { useResizable } from '@/hooks/useResizable'
import { useWindowManagerSafe } from '@/contexts/WindowManagerContext'
import { useIsMobile } from '@/hooks/useIsMobile'

export type WindowSize = 'small' | 'medium' | 'large' | 'full'

// Default sizes for each window size preset
const SIZE_PRESETS: Record<WindowSize, { width: number; height: number }> = {
  small: { width: 320, height: 240 },
  medium: { width: 480, height: 400 },
  large: { width: 800, height: 700 },
  full: { width: 1200, height: 900 },
}

export interface MacStudioWindowProps {
  /** Unique window ID for window management */
  windowId?: string
  title: string
  children: ReactNode
  size?: WindowSize
  isOpen?: boolean
  isFocused?: boolean
  className?: string
  contentClassName?: string
  noPadding?: boolean
  showTrafficLights?: boolean
  /** Enable dragging (desktop only) */
  draggable?: boolean
  /** Enable resizing (desktop only) */
  resizable?: boolean
  /** Initial position (only used if windowId provided) */
  initialPosition?: { x: number; y: number }
  toolbarContent?: ReactNode
  onClose?: () => void
  onMinimize?: () => void
  onMaximize?: () => void
  onFocus?: () => void
}

export function MacStudioWindow({
  windowId,
  title,
  children,
  size = 'medium',
  isOpen = true,
  isFocused = false,
  className = '',
  contentClassName = '',
  noPadding = false,
  showTrafficLights = true,
  draggable = true,
  resizable = true,
  initialPosition,
  toolbarContent,
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
}: MacStudioWindowProps) {
  const isMobile = useIsMobile()
  const windowManager = useWindowManagerSafe()
  const isManaged = windowId && windowManager?.isEnabled && !isMobile

  const [animationState, setAnimationState] = useState<'opening' | 'open' | 'closing' | 'closed'>(
    isOpen ? 'opening' : 'closed'
  )

  // Get window state from manager if managed
  const windowState = isManaged ? windowManager.getWindowState(windowId) : undefined

  // Calculate initial position and size
  const computedInitialPosition = useMemo(() => {
    if (windowState?.position) return windowState.position
    if (initialPosition) return initialPosition
    return { x: 100, y: 80 }
  }, [windowState?.position, initialPosition])

  const computedInitialSize = useMemo(() => {
    if (windowState?.size) return windowState.size
    return SIZE_PRESETS[size]
  }, [windowState?.size, size])

  // Register window with manager
  useEffect(() => {
    if (isManaged && !windowState) {
      windowManager.registerWindow(windowId, computedInitialPosition, computedInitialSize)
    }
    return () => {
      if (isManaged) {
        // Don't unregister on unmount - let positions persist
        // windowManager.unregisterWindow(windowId)
      }
    }
  }, [isManaged, windowId, windowManager, windowState, computedInitialPosition, computedInitialSize])

  // Handle focus
  const handleFocus = useCallback(() => {
    if (isManaged) {
      windowManager.focusWindow(windowId)
    }
    onFocus?.()
  }, [isManaged, windowManager, windowId, onFocus])

  // Draggable hook
  const {
    dragHandleProps,
    containerProps: dragContainerProps,
    position,
    isDragging,
  } = useDraggable({
    initialPosition: computedInitialPosition,
    enabled: Boolean(isManaged && draggable),
    onDragEnd: (pos) => {
      if (isManaged) {
        windowManager.updatePosition(windowId, pos)
      }
    },
    onFocus: handleFocus,
  })

  // Resizable hook
  const {
    getResizeHandleProps,
    size: currentSize,
    isResizing,
  } = useResizable({
    initialSize: computedInitialSize,
    minSize: windowManager?.minDimensions ?? { width: 200, height: 150 },
    position,
    enabled: Boolean(isManaged && resizable),
    onResize: (newSize, newPos) => {
      if (isManaged) {
        windowManager.updateSize(windowId, newSize)
        if (newPos) {
          windowManager.updatePosition(windowId, newPos)
        }
      }
    },
  })

  // Animation state management
  useEffect(() => {
    if (isOpen && animationState === 'closed') {
      setAnimationState('opening')
      const timer = setTimeout(() => setAnimationState('open'), 200)
      return () => clearTimeout(timer)
    } else if (!isOpen && (animationState === 'open' || animationState === 'opening')) {
      setAnimationState('closing')
      const timer = setTimeout(() => setAnimationState('closed'), 150)
      return () => clearTimeout(timer)
    }
  }, [isOpen, animationState])

  if (animationState === 'closed') {
    return null
  }

  // Check if minimized
  if (windowState?.isMinimized) {
    return null
  }

  const effectiveFocused = isManaged
    ? windowManager.focusedWindowId === windowId
    : isFocused

  const windowClasses = [
    'mac-studio-window',
    `mac-studio-window--${size}`,
    effectiveFocused && 'mac-studio-window--focused',
    animationState === 'opening' && 'mac-studio-window--opening',
    animationState === 'closing' && 'mac-studio-window--closing',
    isManaged && 'mac-studio-window--managed',
    isDragging && 'mac-studio-window--dragging',
    isResizing && 'mac-studio-window--resizing',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const contentClasses = [
    'mac-studio-window-content',
    noPadding && 'mac-studio-window-content--no-padding',
    contentClassName,
  ]
    .filter(Boolean)
    .join(' ')

  // Compute window style - use explicit width/height for size presets
  const windowStyle: React.CSSProperties = isManaged
    ? {
        ...dragContainerProps.style,
        // Apply exact size from presets or currentSize for managed windows
        width: windowState?.isMaximized ? '100%' : currentSize.width,
        height: windowState?.isMaximized ? '100%' : currentSize.height,
        zIndex: windowState?.zIndex ?? 1,
        maxWidth: 'none',
        // Ensure minimum dimensions are respected
        minWidth: windowManager?.minDimensions?.width ?? 200,
        minHeight: windowManager?.minDimensions?.height ?? 150,
      }
    : {}

  // Resize handles for managed windows
  const resizeHandles = isManaged && resizable && !windowState?.isMaximized && (
    <>
      <div className="mac-window-resize mac-window-resize--n" {...getResizeHandleProps('n')} />
      <div className="mac-window-resize mac-window-resize--s" {...getResizeHandleProps('s')} />
      <div className="mac-window-resize mac-window-resize--e" {...getResizeHandleProps('e')} />
      <div className="mac-window-resize mac-window-resize--w" {...getResizeHandleProps('w')} />
      <div className="mac-window-resize mac-window-resize--ne" {...getResizeHandleProps('ne')} />
      <div className="mac-window-resize mac-window-resize--nw" {...getResizeHandleProps('nw')} />
      <div className="mac-window-resize mac-window-resize--se" {...getResizeHandleProps('se')} />
      <div className="mac-window-resize mac-window-resize--sw" {...getResizeHandleProps('sw')} />
    </>
  )

  return (
    <div
      ref={isManaged ? dragContainerProps.ref : undefined}
      className={windowClasses}
      style={windowStyle}
      onClick={handleFocus}
      role="dialog"
      aria-label={title}
    >
      {/* Resize Handles */}
      {resizeHandles}

      {/* Title Bar */}
      <div
        className="mac-studio-titlebar"
        {...(isManaged && draggable ? dragHandleProps : {})}
      >
        {showTrafficLights && (
          <TrafficLights
            onClose={onClose}
            onMinimize={() => {
              if (isManaged) {
                windowManager.minimizeWindow(windowId)
              }
              onMinimize?.()
            }}
            onMaximize={() => {
              if (isManaged) {
                if (windowState?.isMaximized) {
                  windowManager.restoreWindow(windowId)
                } else {
                  windowManager.maximizeWindow(windowId)
                }
              }
              onMaximize?.()
            }}
          />
        )}

        <span className="mac-studio-window-title">{title}</span>

        {toolbarContent && (
          <div className="mac-studio-toolbar">
            {toolbarContent}
          </div>
        )}
      </div>

      {/* Content */}
      <div className={contentClasses}>
        {children}
      </div>
    </div>
  )
}
